import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteReleaseOwnershipMutation, getReleaseQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteReleaseOwnership = (releaseId, setOwnerships, withoutAlert) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteReleaseOwnershipAction, { loading }] = useMutation(deleteReleaseOwnershipMutation, {
        ...(releaseId && {
            awaitRefetchQueries: true,
            refetchQueries: [
                {
                    query: getReleaseQuery,
                    variables: {
                        where: { id: releaseId },
                    },
                },
            ],
        }),
    });

    const { deleteReleaseOwnershipPending: deleteReleaseOwnershipPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteReleaseOwnership = async (id) => {
        try {
            await deleteReleaseOwnershipAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", {
                                entity: `${t("releases.entity")} ${t("labels.ownership")}`,
                            }),
                            statusTypesData.success,
                        ),
                    );

                    if (setOwnerships) {
                        setOwnerships((ownerships) => [
                            ...ownerships.filter(({ release }) => release?.ownership?.id !== id),
                        ]);
                    }
                },
                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );

            return { error: err };
        } finally {
            toast.dismiss(`${deleteReleaseOwnershipPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && withoutAlert) {
                toast.info(
                    () =>
                        t("messages.deleteMsgs.pending", {
                            entity: `${t("releases.entity")} ${t("labels.ownership")}`,
                        }),
                    { toastId: `${deleteReleaseOwnershipPendingToastName}_${toastId}` },
                );
            }
        },
        [loading], //eslint-disable-line
    );

    return {
        deleteReleaseOwnership,
        loading,
    };
};

export default useDeleteReleaseOwnership;
